import { forwardRef, useEffect, useRef, useState } from "react";
// import moment from "moment-timezone";
import { TextField } from "@mui/material";
import moment from "moment";
import { useSnackbar } from "notistack";

const MS_PER_MINUTE = 60000;

const EditableCellTime = forwardRef(
  ({ label, eventDate, time, setTime, onKeyDown, disableClear, fieldName, disabled }, ref) => {
    const { enqueueSnackbar } = useSnackbar();

    const displayFromTime = (t) => {
      return moment(t).format("hh:mm a");
    };
    const [displayTime, setDisplayTime] = useState(time ? displayFromTime(time) : "");

    useEffect(() => {
      setDisplayTime(time ? displayFromTime(time) : "");
    }, [time]);

    function parseTime(timeStr) {
      if (!timeStr || typeof timeStr !== "string") {
        return { hours: null, minutes: null };
      }

      // Clean and normalize the input
      const cleanStr = timeStr.trim().toLowerCase();

      // Regular expressions to match different time patterns
      const fullTimeRegex = /^(\d{1,2}):(\d{2})\s*(am|pm|AM|PM|a|A|p|P)?$/;
      const shortTimeRegex = /^(\d{1,2})\s*(am|pm|AM|PM|a|A|p|P)?$/;

      let hours;
      let minutes;
      let ampm;

      // Try full time format first (HH:MM)
      let match = cleanStr.match(fullTimeRegex);
      if (match) {
        hours = parseInt(match[1], 10);
        minutes = parseInt(match[2], 10);
        // eslint-disable-next-line prefer-destructuring
        ampm = match[3];
      } else {
        // Try short time format (HH only)
        match = cleanStr.match(shortTimeRegex);
        if (match) {
          hours = parseInt(match[1], 10);
          minutes = 0; // Default to 0 minutes
          // eslint-disable-next-line prefer-destructuring
          ampm = match[2]?.toLowerCase();
        } else {
          return { hours: null, minutes: null };
        }
      }

      // Validate minutes (only if we parsed minutes from full format)
      if (minutes < 0 || minutes > 59) {
        return { hours: null, minutes: null };
      }

      // If hours > 12, treat as 24-hour format regardless of AM/PM
      if (hours > 12) {
        if (hours > 23) {
          return { hours: null, minutes: null };
        }
        // Already in 24-hour format
        return { hours, minutes };
      }

      // Handle AM/PM conversion for hours <= 12
      if (ampm) {
        if (hours < 1 || hours > 12) {
          return { hours: null, minutes: null };
        }

        if (ampm === "am" || ampm === "a") {
          hours = hours === 12 ? 0 : hours;
        } else if (ampm === "pm" || ampm === "p") {
          hours = hours === 12 ? 12 : hours + 12;
        }
      } else if (hours < 0 || hours > 23) {
        return { hours: null, minutes: null };
      }

      return { hours, minutes };
    }

    const handleBlur = (e) => {
      const tm = e.target.value;
      if (!tm) {
        if (disableClear) {
          // Clearing the field not allowed
          enqueueSnackbar(`${fieldName} can't be empty`, { variant: "error" });
          setDisplayTime(time ? displayFromTime(time) : "");
          return;
        }
        setTime(null);
        setDisplayTime("");
        return;
      }
      const { hours, minutes } = parseTime(tm);

      const eventDateMoment = moment(eventDate);
      const eventDateLocalISOString = eventDateMoment.toISOString(true);
      const eventDateLocalString = eventDateLocalISOString.split("T")[0];

      const hrs = hours < 10 ? `0${hours?.toString()}` : hours?.toString();
      const mns = minutes < 10 ? `0${minutes?.toString()}` : minutes?.toString();
      const newTimeMoment = moment(`${eventDateLocalString}T${hrs}:${mns}:00`);
      if (!newTimeMoment.isValid()) {
        setTime(null);
        setDisplayTime("");
        return;
      }
      const newTime = newTimeMoment.toDate();
      setTime(newTime);
      setDisplayTime(displayFromTime(newTime));
    };

    const cleanInput = (value) => {
      const cleaned = value.replace(/[^0-9:aApPmM]/g, "");

      return cleaned;
    };

    return (
      <>
        <TextField
          label={label}
          id="edit-time-basic"
          variant="outlined"
          inputRef={ref}
          value={displayTime}
          onChange={(e) => {
            setDisplayTime(cleanInput(e.target.value));
          }}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          inputProps={{
            autoComplete: "off",
            autoCorrect: "off",
            autoCapitalize: "off",
            spellCheck: false,
          }}
          sx={{ width: "100%" }}
          disabled={disabled}
        />
      </>
    );
  }
);

export default EditableCellTime;
